import { fromCompanies, fromAwardedCompanies, toCompanyClients, toTeams } from 'utils/serializers'
import cloneDeep from 'lodash/cloneDeep'

export const fromTemplates = templates =>
  templates.map(template => ({
    value: template.id,
    label: template.name
  }))

export const toCompaniesWithClients = companies =>
  companies.map(company => {
    return {
      companyId: company.value,
      dueDate: company.dueDate || '',
      followUpDate: company.followUpDate || '',
      dealContacts: toContacts(company.selectedContacts || []),
      ...(company.invitedToBid !== undefined && { invitedToBid: company.invitedToBid }),
      ...(company.listedWithThisClient !== undefined && {
        listedWithThisClient: company.listedWithThisClient
      })
    }
  })

const toContacts = contacts =>
  contacts.map(contact => {
    let role = contact.role || null
    if (typeof contact.role === 'object' && contact.role !== null) {
      role = contact.role.value
    }
    return {
      contactId: contact.value,
      isMain: contact.isMain || false,
      role
    }
  })

export const toDeal = deal => {
  return {
    address: deal.address,
    address2: deal.address2,
    street2: deal.street2,
    bidStatus: deal.bidStatus?.value,
    briefNote: deal.briefNote,
    distanceToSite: deal.distanceToSite,
    city: deal.city,
    name: deal.name,
    state: deal.state,
    country: deal.country,
    zipCode: deal.zipCode,
    latitude: deal.latitude,
    longitude: deal.longitude,
    status:
      deal.status !== null
        ? typeof deal.status === 'string'
          ? deal.status
          : deal.status.value
        : null,
    workType:
      deal.workType !== null
        ? typeof deal.workType === 'string'
          ? deal.workType
          : deal.workType.value
        : null,
    priority:
      deal.priority !== null
        ? typeof deal.priority === 'string'
          ? deal.priority
          : deal.priority.value
        : null,
    health:
      deal.health !== null
        ? typeof deal.health === 'string'
          ? deal.health
          : deal.health.value
        : null,
    dealCompanies: toCompaniesWithClients(deal.clients),
    size: deal.size !== null ? (typeof deal.size === 'string' ? deal.size : deal.size.value) : null,
    dealCategory: deal.dealCategory !== null ? deal.dealCategory.code : null,
    dealSource: deal.dealSource !== null ? deal.dealSource.code : null,
    dealScopes: deal.dealScopes !== null ? deal.dealScopes.map(scope => scope.value) : null,
    buildingOwners:
      deal.buildingOwners !== null ? deal.buildingOwners.map(scope => scope.value) : null,
    endUsers: deal.endUsers !== null ? deal.endUsers.map(scope => scope.value) : null,
    propertyManagers:
      deal.propertyManagers !== null ? deal.propertyManagers.map(scope => scope.value) : null,
    noWinReason: deal.noWinReason !== null ? deal.noWinReason.code : null,
    squarefootage: deal.squarefootage,
    stories: deal.stories,
    bidReferenceDocument: deal.bidReferenceDocument,
    quoteNumber: deal.quoteNumber,
    owner: deal.owner,
    developer: deal.developer,
    generalcontractor: deal.generalcontractor,
    architect: deal.architect,
    structuraleng: deal.structuraleng,
    constructionmgr: deal.constructionmgr,
    civileng: deal.civileng,
    mechanicaleng: deal.mechanicaleng,
    electricaleng: deal.electricaleng,
    plumbingeng: deal.plumbingeng,
    fpeng: deal.fpeng,
    othereng: deal.othereng,
    dealmanagerid: deal.dealManager?.value,
    dueDate: deal.dueDate,
    submissionDate: deal.submissionDate,
    projectStartDate: deal.projectStartDate,
    projectEndDate: deal.projectEndDate,
    dealProjectStatusId: deal.dealProjectStatus?.value ?? null,
    followUpDate: deal.followUpDate,
    wonOn: deal.wonOn,
    lostOn: deal.lostOn,
    awardedCompanyId: deal.awardedCompany ? deal.awardedCompany.value : null,
    description: deal.description,
    hardBid: deal.hardBid,
    sealedBid: deal.sealedBid,
    prevailingWage: deal.prevailingWage,
    visibility: deal.visibility?.value ?? null,
    internalBidStatus: deal.internalBidStatus?.value,
    teams: deal.teams ? deal.teams.map(t => t.value) : null,
    followUpCalendar: deal.followUpCalendar,
    linkToPlans: deal.linkToPlans,
    closingChance: deal.closingChance,
    addToForecastReports: deal.addToForecastReports,
    geographicalAreaId: deal.geographicalArea?.value ?? null,
    notBiddingReasonId: deal.dealNotBiddingReason?.value ?? null,
    dealContractTypeId: deal.dealContractType?.value ?? null,
    projectNumber: deal.projectNumber ?? ''
  }
}

export const toCompanies = companies => companies.map(company => company.value)

export const toDocumentFormData = (companyId, documents, folderId) => {
  const formData = new FormData()
  formData.append('folderId', folderId)
  formData.append('companyId', companyId)
  formData.append('tags', '')
  documents.forEach(document => formData.append('document', document))

  return formData
}

export const fromManager = manager => ({
  value: manager.id,
  label: manager.firstName + ' ' + manager.lastName,
  organization: manager.organization
})

export const toDealsByCompany = deals =>
  deals.map(deal => ({
    id: deal.id,
    name: deal.name,
    dealManager: deal.dealManager,
    status: deal.status
  }))

export const toFormData = (contactId, files, note, dealId, dateEntered) => {
  const formData = new FormData()
  formData.append('contactId', contactId)
  formData.append('dealId', dealId)
  formData.append('note', note)
  formData.append('dateEntered', dateEntered)
  files.forEach(file => {
    if (!file.id) {
      formData.append('newAttachments', file)
    } else {
      formData.append('attachmentIds', file.id)
    }
  })

  return formData
}

export const toGroupOptions = contacts => {
  return contacts.map(contact => ({
    label: contact.companyName,
    options: contact.contacts.map(c => ({
      label: c.contactName,
      value: c.contactId
    }))
  }))
}

export const toCategories = categories =>
  categories.map(category => ({
    value: category.id,
    label: category.name,
    system: category.system,
    code: category.code
  }))

export const augumentDealObject = (deal, additionalProperties) => {
  const sortedCompanies = deal.dealCompanies.sort((a, b) =>
    a.company.legalName > b.company.legalName ? 1 : -1
  )
  deal.clients = toCompanyClients(sortedCompanies)
  deal.dealCompanies = fromCompanies(sortedCompanies)
  deal.awardedCompany = fromAwardedCompanies(deal.awardedCompany)
  deal.dealManager = fromManager(deal.dealManager)
  deal.statusCategory = deal.status.category
  deal.status = {
    value: deal.status.code,
    label: deal.status.name
  }
  deal.dealCategory = deal.dealCategory
    ? {
        value: deal.dealCategory.id,
        label: deal.dealCategory.name
      }
    : null
  deal.dealProjectStatus = deal.dealProjectStatus
    ? {
        value: deal.dealProjectStatus.id,
        label: deal.dealProjectStatus.name
      }
    : null
  deal.workType = deal.workType
    ? {
        value: deal.workType.id,
        label: deal.workType.name
      }
    : null
  deal.bidStatus = deal.bidStatus
    ? {
        value: deal.bidStatus.id,
        label: deal.bidStatus.name
      }
    : null
  deal.internalBidStatus = deal.internalBidStatus
    ? {
        value: deal.internalBidStatus.id,
        label: deal.internalBidStatus.name
      }
    : null
  if (deal.dealSource) {
    deal.dealSource = {
      value: deal.dealSource.id,
      label: deal.dealSource.name
    }
  }
  if (deal.dealScopes) {
    deal.dealScopes = deal.dealScopes.map(s => ({
      value: s.id,
      label: s.name
    }))
  }
  if (deal.visibility) {
    deal.visibility = {
      value: deal.visibility,
      label: deal.visibility === 'PUBLIC' ? 'Public' : 'Private'
    }
  }
  if (deal.noWinReason) {
    deal.noWinReason = {
      value: deal.noWinReason.id,
      label: deal.noWinReason.name
    }
  }
  if (deal.teams) {
    deal.teams = toTeams(deal.teams)
  }

  deal.time = cloneDeep(deal.dueDate)

  if (deal.geographicalArea) {
    deal.geographicalArea = {
      value: deal.geographicalArea.id,
      label: deal.geographicalArea.name
    }
  }

  if (additionalProperties) {
    if (additionalProperties.companiesWithType) {
      const { buildingOwners, endUsers, propertyManagers } = additionalProperties.companiesWithType
      deal.buildingOwners = buildingOwners.map(s => ({
        value: s.id,
        label: s.name
      }))
      deal.endUsers = endUsers.map(s => ({
        value: s.id,
        label: s.name
      }))
      deal.propertyManagers = propertyManagers.map(s => ({
        value: s.id,
        label: s.name
      }))
    }
  }

  if (deal.dealNotBiddingReason) {
    deal.dealNotBiddingReason = {
      value: deal.dealNotBiddingReason.id,
      label: deal.dealNotBiddingReason.name
    }
  }

  if (deal.dealContractType) {
    deal.dealContractType = {
      value: deal.dealContractType.id,
      label: deal.dealContractType.name
    }
  }

  // this hack is because BE sends proposals null and should be an array
  if (!deal.proposals) {
    deal.proposals = []
  }

  // this hacks are all over the place due to bad backend
  // remove the notes from the deal object, as they are always null
  // and it overwrites the actual value for the notes
  delete deal.notes

  return deal
}

export const toDealDetails = deal => {
  const {
    addToForecastReports,
    closingChance,
    cost,
    value,
    forecastRevenue,
    dealManager,
    forecastGrossProfit,
    monthlyQuota,
    yearlyQuota,
    monthlyTargetProfit,
    yearlyTargetProfit,
    grossProfit,
    margin,
    markup,
    overallProjectValue,
    overallProjectValuePercent,
    signedContractValue,
    signedContractValuePercent,
    expectedCloseDate
  } = deal
  return {
    addToForecastReports,
    closingChance,
    forecastRevenue,
    forecastGrossProfit,
    dealManager,
    cost,
    value,
    monthlyQuota,
    yearlyQuota,
    monthlyTargetProfit,
    yearlyTargetProfit,
    grossProfit,
    margin,
    markup,
    overallProjectValue,
    overallProjectValuePercent,
    signedContractValue,
    signedContractValuePercent,
    expectedCloseDate
  }
}
